import { createContext, useContext, useEffect, useState } from "react";
import { Sun, Moon } from "@phosphor-icons/react";

const ThemeContext = createContext({ theme: "light", toggleTheme: () => {} });

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem("pos_theme") || "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("pos_theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeToggle = ({ inline = false, compact = false }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  const base = "flex items-center gap-2 rounded-full border border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted transition-all duration-200 active:scale-95";
  const layout = inline
    ? (compact ? "h-8 w-8 justify-center" : "h-9 px-3 shadow-sm hover:shadow")
    : "fixed top-3 right-3 z-[100] h-10 px-3 shadow-md hover:shadow-lg";

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="theme-toggle-btn"
      className={`${base} ${layout}`}
    >
      {isDark ? (
        <Sun size={16} weight="fill" className="text-amber-400" />
      ) : (
        <Moon size={16} weight="fill" className="text-primary" />
      )}
      {!compact && (
        <span className="hidden lg:inline text-xs font-medium uppercase tracking-wide">
          {isDark ? "Light" : "Dark"}
        </span>
      )}
    </button>
  );
};
